// ======================================================================
// FILE: src/modules/wms/escalation/pages/EscalationPolicyListPage.js
// MODULE: Work Management System (WMS)
// FEATURE: Escalation Management
//
// PURPOSE:
//   Manage Escalation Policies
//   Search Escalation Policies
//   View Escalation Summary
//
// ROUTES:
//   /wms/escalation-policies
//
// DEPENDENCIES:
//   wmsEscalationService
//
// ARCHITECTURE:
//   ✓ TaskFlow ERP Standard
//   ✓ AdminLTE
//   ✓ Multi Tenant
//   ✓ Multi Location
//   ✓ Audit Enabled
// ======================================================================

import React, {
  useEffect,
  useState,
} from "react";

import {
  useNavigate,
} from "react-router-dom";

import wmsEscalationService
from "../../services/wmsEscalationService";

export default function EscalationPolicyListPage() {

  const navigate = useNavigate();

  const [loading, setLoading] =
    useState(false);

  const [rows, setRows] =
    useState([]);

  const [pagination, setPagination] =
    useState({});

  const [summary, setSummary] =
    useState({

      totalPolicies: 0,
      activePolicies: 0,
      totalLevels: 0,
      totalRecipients: 0,
    });

  const [filters, setFilters] =
    useState({

      q: "",
      status: "",

      page: 1,
      per_page: 20,
    });

  // ==========================================================
  // LOAD
  // ==========================================================

  useEffect(() => {

    loadData();

  }, [filters.page]);

  async function loadData() {

    try {

      setLoading(true);

      const result =
        await wmsEscalationService
          .listEscalationPolicies(
            filters
          );

      const items =
        result.items || [];

      setRows(items);

      setPagination(
        result.pagination || {}
      );

      setSummary({

        totalPolicies:
          items.length,

        activePolicies:
          items.filter(
            x => x.is_active
          ).length,

        totalLevels:
          items.reduce(
            (a, b) =>
              a +
              (
                b.level_count || 0
              ),
            0
          ),

        totalRecipients:
          items.reduce(
            (a, b) =>
              a +
              (
                b.recipient_count || 0
              ),
            0
          ),
      });

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Failed to load escalation policies"
      );

    } finally {

      setLoading(false);
    }
  }

  // ==========================================================
  // DELETE
  // ==========================================================

  async function handleDelete(
    row
  ) {

    const ok =
      window.confirm(
        `Delete Escalation Policy ${row.escalation_policy_name}?`
      );

    if (!ok) return;

    try {

      await wmsEscalationService
        .deleteEscalationPolicy(
          row.id
        );

      loadData();

    } catch (err) {

      alert(
        err?.response?.data?.message
        ||
        "Delete failed"
      );
    }
  }

  // ==========================================================
  // FILTERS
  // ==========================================================

  function applyFilters() {

    setFilters(prev => ({
      ...prev,
      page: 1,
    }));

    loadData();
  }

  function clearFilters() {

    setFilters({

      q: "",
      status: "",

      page: 1,
      per_page: 20,
    });

    loadData();
  }

  // ==========================================================
  // UI
  // ==========================================================

  return (

    <div className="container-fluid">

      {/* ===================================================== */}
      {/* HEADER */}
      {/* ===================================================== */}

      <div
        className="card border-0 mb-3"
        style={{
          background:
            "linear-gradient(90deg,#0d6efd,#4f8cff)",
          color: "#fff",
        }}
      >

        <div className="card-body d-flex justify-content-between align-items-center">

          <div>

            <h3 className="mb-1">
              Escalation Management
            </h3>

            <div>
              Configure Escalation Policies,
              Levels and Notifications
            </div>

          </div>

          <div>

            <button
              className="btn btn-light me-2"
              onClick={loadData}
            >
              Refresh
            </button>

            <button
              className="btn btn-light"
              onClick={() =>
                navigate(
                  "/wms/escalation-policies/create"
                )
              }
            >
              + Create Escalation Policy
            </button>

          </div>

        </div>

      </div>

      {/* ===================================================== */}
      {/* KPI CARDS */}
      {/* ===================================================== */}

      <div className="row">

        <div className="col-md-3">

          <div
            className="card shadow-sm"
            style={{
              borderTop:
                "3px solid #0d6efd",
              borderRadius: "10px",
            }}
          >

            <div className="card-body">

              <div className="d-flex justify-content-between">

                <div>

                  <small
                    className="text-muted"
                  >
                    Total Policies
                  </small>

                  <h2 className="mt-2 mb-0">
                    {
                      summary.totalPolicies
                    }
                  </h2>

                </div>

                <div
                  style={{
                    fontSize: "28px",
                  }}
                >
                  📋
                </div>

              </div>

            </div>

          </div>

        </div>

        <div className="col-md-3">

          <div
            className="card shadow-sm"
            style={{
              borderTop:
                "3px solid #28a745",
              borderRadius: "10px",
            }}
          >

            <div className="card-body">

              <div className="d-flex justify-content-between">

                <div>

                  <small
                    className="text-muted"
                  >
                    Active Policies
                  </small>

                  <h2 className="mt-2 mb-0">
                    {
                      summary.activePolicies
                    }
                  </h2>

                </div>

                <div
                  style={{
                    fontSize: "28px",
                  }}
                >
                  ✅
                </div>

              </div>

            </div>

          </div>

        </div>
                <div className="col-md-3">

          <div
            className="card shadow-sm"
            style={{
              borderTop:
                "3px solid #fd7e14",
              borderRadius: "10px",
            }}
          >

            <div className="card-body">

              <div className="d-flex justify-content-between">

                <div>

                  <small
                    className="text-muted"
                  >
                    Total Levels
                  </small>

                  <h2 className="mt-2 mb-0">
                    {
                      summary.totalLevels
                    }
                  </h2>

                </div>

                <div
                  style={{
                    fontSize: "28px",
                  }}
                >
                  📈
                </div>

              </div>

            </div>

          </div>

        </div>

        <div className="col-md-3">

          <div
            className="card shadow-sm"
            style={{
              borderTop:
                "3px solid #dc3545",
              borderRadius: "10px",
            }}
          >

            <div className="card-body">

              <div className="d-flex justify-content-between">

                <div>

                  <small
                    className="text-muted"
                  >
                    Total Recipients
                  </small>

                  <h2 className="mt-2 mb-0">
                    {
                      summary.totalRecipients
                    }
                  </h2>

                </div>

                <div
                  style={{
                    fontSize: "28px",
                  }}
                >
                  👥
                </div>

              </div>

            </div>

          </div>

        </div>

      </div>

      {/* ===================================================== */}
      {/* SEARCH PANEL */}
      {/* ===================================================== */}

      <div
        className="card shadow-sm mb-3"
        style={{
          borderRadius: "10px",
        }}
      >

        <div className="card-header">

          <strong>
            Search & Filters
          </strong>

        </div>

        <div className="card-body">

          <div className="row">

            <div className="col-md-6">

              <label>
                Search
              </label>

              <input
                className="form-control"
                placeholder="Code / Name"
                value={filters.q}
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    q: e.target.value,
                  })
                }
              />

            </div>

            <div className="col-md-3">

              <label>
                Status
              </label>

              <select
                className="form-control"
                value={
                  filters.status
                }
                onChange={(e) =>
                  setFilters({
                    ...filters,
                    status:
                      e.target.value,
                  })
                }
              >

                <option value="">
                  All
                </option>

                <option value="ACTIVE">
                  Active
                </option>

                <option value="INACTIVE">
                  Inactive
                </option>

              </select>

            </div>

          </div>

          <div className="mt-3 text-end">

            <button
              className="btn btn-light me-2"
              onClick={clearFilters}
            >
              Clear
            </button>

            <button
              className="btn btn-primary"
              onClick={applyFilters}
            >
              Apply Filters
            </button>

          </div>

        </div>

      </div>

      {/* ===================================================== */}
      {/* GRID */}
      {/* ===================================================== */}

      <div className="card">

        <div className="card-body p-0">

          <table
            className="table table-hover table-striped align-middle mb-0"
          >

            <thead className="table-light">

              <tr>

                <th>Code</th>

                <th>Name</th>

                <th>SLA Policy</th>

                <th>Levels</th>

                <th>Recipients</th>

                <th>Status</th>

                <th width="180">
                  Actions
                </th>

              </tr>

            </thead>

            <tbody>

              {loading && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    Loading...
                  </td>

                </tr>

              )}

              {!loading &&
                rows.length === 0 && (

                <tr>

                  <td
                    colSpan="7"
                    className="text-center"
                  >
                    No Records Found
                  </td>

                </tr>

              )}

              {!loading &&
                rows.map((row) => (

                <tr key={row.id}>

                  <td>
                    {
                      row.escalation_policy_code
                    }
                  </td>

                  <td>
                    {
                      row.escalation_policy_name
                    }
                  </td>

                  <td>
                    {
                      row.sla_policy_name
                    }
                  </td>

                  <td>
                    {
                      row.level_count || 0
                    }
                  </td>

                  <td>
                    {
                      row.recipient_count || 0
                    }
                  </td>

                  <td>

                    <span
                      className={
                        row.is_active
                          ? "badge bg-success"
                          : "badge bg-danger"
                      }
                    >
                      {
                        row.is_active
                          ? "Active"
                          : "Inactive"
                      }
                    </span>

                  </td>

                  <td>

                    <button
                      className="btn btn-sm btn-primary me-1"
                      onClick={() =>
                        navigate(
                          `/wms/escalation-policies/${row.id}/edit`
                        )
                      }
                    >
                      Open
                    </button>

                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() =>
                        handleDelete(
                          row
                        )
                      }
                    >
                      Delete
                    </button>

                  </td>

                </tr>

              ))}

            </tbody>

          </table>

        </div>

      </div>

      {/* ===================================================== */}
      {/* PAGINATION */}
      {/* ===================================================== */}

      <div className="mt-3 d-flex justify-content-between">

        <div>

          Total Records:

          {" "}

          {
            pagination.total || 0
          }

        </div>

        <div>

          Page

          {" "}

          {
            pagination.page || 1
          }

        </div>

      </div>

    </div>
  );
}